import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select as ChakraSelect,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AsyncSelect, Select as RSelect } from 'chakra-react-select';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  CaretLeft,
  CaretRight,
  CheckCircle,
  ListChecks,
  LockSimpleOpen,
  MagnifyingGlass,
  Sparkle,
  WarningCircle,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import {
  criarBuscaEmpresasModo,
  EmpresaBuscaModo,
  IEmpresaOption,
} from '../../../../../hooks/useContaCilImportar';
import {
  IContaCilLancamento,
  IContaCilNotaSugerida,
  ILancamentosFiltro,
  IPocOption,
  useContaCilLancamentos,
  useContaCilNotasSugeridas,
  useContaCilPocs,
  useContaCilUpdateLancamento,
} from '../../../../../hooks/useContaCilLancamentos';
import { useContaCilContas } from '../../../../../hooks/useContaCilContasBancarias';
import { IEmpresaSaude, useContaCilEmpresasSaude } from '../../../../../hooks/useContaCilEmpresas';
import { useContaCilSaldosConta } from '../../../../../hooks/useContaCilDashboard';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { api } from '../../../../../services/api';
import { useDialog } from '../../../../../providers/dialog';
import { AutoConciliarModal } from './components/AutoConciliarModal';
import { useAplicarEmpresaParam } from '../useEmpresaParam';
import { ConciliarKpis, FiltroStatus } from './components/ConciliarKpis';
import { EmpresaSaudeCard } from '../empresas/components/EmpresaSaudeCard';
import { LancamentoCard } from './components/LancamentoCard';
import { LancamentoDetailModal } from './components/LancamentoDetailModal';
import { ProgressoConciliacao } from './components/ProgressoConciliacao';
import { QuebrasPanel } from './components/QuebrasPanel';
import {
  PocSelectGroup,
  PocSelectOption,
  PocSelectTooltip,
  POC_COMPONENTS_C,
  POC_COMPONENTS_D,
  STATUS_BADGE,
  fmtData,
  fmtDoc,
  fmtValor,
  pocSelectStyles,
} from './shared';
import { propsMenuSelect } from '../../../../../utils/selectMenu';

const PAGE_SIZE = 25;

const PLACEHOLDER_BUSCA_EMPRESA: Record<EmpresaBuscaModo, string> = {
  razao: 'Digite a razão social...',
  cnpj: 'Digite o CNPJ...',
  id: 'Digite o id (external_id)...',
};

const ANO_ATUAL = new Date().getFullYear();
const ANOS = [ANO_ATUAL, ANO_ATUAL - 1, ANO_ATUAL - 2];
const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

interface LinhaProps {
  lanc: IContaCilLancamento;
  pocOptions: PocSelectOption[];
  pocGroups: PocSelectGroup[];
  nota?: IContaCilNotaSugerida;
  onSaved: () => void;
  onOpen: (lanc: IContaCilLancamento) => void;
}

function LinhaLancamento({ lanc, pocOptions, pocGroups, nota, onSaved, onOpen }: LinhaProps) {
  const toast = useToast();
  const { mutateAsync, isPending } = useContaCilUpdateLancamento();

  const isEntrada = lanc.dc === 'C';
  const contraField: 'debito_poc' | 'credito_poc' = isEntrada ? 'credito_poc' : 'debito_poc';
  const contraPocCod = isEntrada ? lanc.credito_poc : lanc.debito_poc;
  // Perna-banco: se a conta-banco não tem POC no plano, fica vazia -> pendente.
  const bankPocCod = isEntrada ? lanc.debito_poc : lanc.credito_poc;
  const faltaContaBanco = lanc.status !== 'conciliado' && !bankPocCod;
  const contraValue = useMemo(
    () => pocOptions.find(o => o.value === contraPocCod) ?? null,
    [pocOptions, contraPocCod],
  );

  const salvar = async (campo: 'debito_poc' | 'credito_poc', valor: string | null) => {
    try {
      await mutateAsync({ id: lanc.id, [campo]: valor });
      onSaved();
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao vincular POC',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  // Aceita a contrapartida JÁ preenchida em 1 clique, sem abrir o modal —
  // mesma ação do "Concluir": reenvia a perna não-banco e o status recalcula
  // pra conciliado. Vale pra REVISÃO e pendentes pré-preenchidos.
  const aceitar = async () => {
    if (!contraPocCod) return;
    try {
      await mutateAsync({ id: lanc.id, [contraField]: contraPocCod });
      onSaved();
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao aceitar',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  const badge = STATUS_BADGE[lanc.status] ?? { cor: 'gray', label: lanc.status };
  const corValor = lanc.dc === 'C' ? '#10B981' : '#EF4444';
  const subInfo = [
    lanc.tipo_operacao ? lanc.tipo_operacao.toUpperCase() : null,
    lanc.categoria,
    lanc.banco_nome ? `${lanc.banco_nome}${lanc.conta ? ` ${lanc.conta}` : ''}` : null,
    lanc.documento ? `doc ${lanc.documento}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const pocChakra = {
    control: (p: Record<string, unknown>) => ({ ...p, borderRadius: 'lg', minHeight: '32px' }),
  };

  const isIa = lanc.fonte_match === 'ia';
  // IA ja analisou (dt_ia_tentativa) mas segue pendente e sem contrapartida ->
  // ela nao conseguiu identificar (ex: Pix a PF). Marca pra acao manual.
  const iaNaoIdentificou =
    !!lanc.dt_ia_tentativa && lanc.status === 'pendente' && contraPocCod == null;
  // Extrato de origem com divergência não discriminada pendente: o backend
  // recusa a conciliação (409) até o contador resolver o quiz em Extratos.
  const extratoBloqueado = !!lanc.extrato_bloqueado;
  // Top sugestão da IA pra preencher inline enquanto não há contrapartida.
  const topSug = !contraPocCod ? lanc.ia_alternativas?.[0] : undefined;
  return (
    <Tr
      _hover={{ bg: 'rgba(27, 136, 245, 0.04)' }}
      bg={isIa ? 'rgba(99, 102, 241, 0.05)' : undefined}
      sx={isIa ? { boxShadow: 'inset 3px 0 0 0 #6366F1' } : undefined}
      cursor="pointer"
      tabIndex={0}
      role="button"
      aria-label={`Abrir lançamento ${fmtData(lanc.data)} ${fmtValor(lanc.valor)}`}
      onClick={() => onOpen(lanc)}
      onKeyDown={ev => {
        // Ignora teclas já tratadas por filhos (ex: Enter no select de POC).
        if (ev.defaultPrevented || ev.target !== ev.currentTarget) return;
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          onOpen(lanc);
        }
      }}
    >
      <Td whiteSpace="nowrap" fontSize="xs" color="#475569">
        {fmtData(lanc.data)}
      </Td>
      <Td fontSize="xs" color="#0F172A" maxW="280px">
        <Tooltip label={lanc.historico} hasArrow openDelay={300}>
          <Text isTruncated>{lanc.historico}</Text>
        </Tooltip>
        {lanc.contraparte && (
          <Tooltip
            label={
              lanc.contraparte_cnpj_cpf
                ? `${lanc.contraparte} · ${fmtDoc(lanc.contraparte_cnpj_cpf)}`
                : lanc.contraparte
            }
            hasArrow
            openDelay={300}
          >
            <Text fontSize="11px" color="#1B88F5" fontWeight="600" isTruncated>
              {lanc.contraparte}
              {lanc.contraparte_cnpj_cpf ? ` · ${fmtDoc(lanc.contraparte_cnpj_cpf)}` : ''}
            </Text>
          </Tooltip>
        )}
        {subInfo && (
          <Text fontSize="10px" color="#94A3B8" isTruncated>
            {subInfo}
          </Text>
        )}
      </Td>
      <Td isNumeric whiteSpace="nowrap" fontSize="xs" fontWeight="600" color={corValor}>
        {lanc.dc === 'C' ? '+' : '−'} {fmtValor(lanc.valor)}
      </Td>
      <Td isNumeric whiteSpace="nowrap" fontSize="xs" color="#64748B">
        {lanc.saldo != null ? fmtValor(lanc.saldo) : '—'}
      </Td>
      {/* Célula do select de POC — não abre o modal da linha. */}
      <Td minW="230px" onClick={ev => ev.stopPropagation()}>
        <PocSelectTooltip label={contraValue?.label}>
          <RSelect<PocSelectOption>
            size="sm"
            placeholder="Contrapartida..."
            options={pocGroups}
            value={contraValue}
            onChange={opt => salvar(contraField, opt ? opt.value : null)}
            isClearable
            isDisabled={isPending || extratoBloqueado}
            components={isEntrada ? POC_COMPONENTS_C : POC_COMPONENTS_D}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            {...propsMenuSelect}
            styles={pocSelectStyles}
            chakraStyles={pocChakra}
          />
        </PocSelectTooltip>
        {topSug?.cod && (
          <Flex align="center" gap={1.5} mt={1.5}>
            <Sparkle size={11} weight="fill" color="#6366F1" />
            <Text fontSize="10px" color="#475569" fontWeight="600" isTruncated flex={1} minW={0}>
              {topSug.cod} · {topSug.nm}
            </Text>
            <Button
              size="xs"
              h="20px"
              minW="auto"
              px={2}
              fontSize="10px"
              variant="solid"
              colorScheme="purple"
              borderRadius="md"
              isLoading={isPending}
              isDisabled={extratoBloqueado}
              onClick={() => topSug.cod && salvar(contraField, topSug.cod)}
            >
              Usar
            </Button>
          </Flex>
        )}
      </Td>
      <Td fontSize="11px" maxW="170px">
        {nota ? (
          <Box>
            <Flex align="center" gap="1">
              <Text
                isTruncated
                color={nota.match_cnpj || nota.match_nome ? '#10B981' : '#0F172A'}
                fontWeight={nota.match_cnpj || nota.match_nome ? '600' : '400'}
                title={nota.contraparte ?? ''}
              >
                {nota.contraparte ?? '—'}
              </Text>
              {nota.match_cnpj ? (
                <Badge colorScheme="green" variant="solid" fontSize="8px" px="1">
                  CNPJ ✓
                </Badge>
              ) : (
                nota.match_nome && (
                  <Badge colorScheme="green" variant="subtle" fontSize="8px" px="1">
                    nome ✓
                  </Badge>
                )
              )}
            </Flex>
            <Text fontSize="10px" color="#94A3B8">
              {nota.tipo === 'entrada' ? 'NF entrada' : 'NF saída'} {nota.numero_nf}
              {nota.candidatos > 1 ? ` · +${nota.candidatos - 1}` : ''}
            </Text>
          </Box>
        ) : (
          <Text color="#CBD5E1">—</Text>
        )}
      </Td>
      <Td>
        <Flex align="center" gap={1} flexWrap="wrap">
          <Badge colorScheme={badge.cor} borderRadius="full" px={2}>
            {badge.label}
          </Badge>
          {contraPocCod && bankPocCod && lanc.status !== 'conciliado' && !extratoBloqueado && (
            <Button
              size="xs"
              h="20px"
              px={2}
              borderRadius="full"
              colorScheme="green"
              fontSize="10px"
              fontWeight="700"
              leftIcon={<CheckCircle size={11} weight="fill" />}
              isLoading={isPending}
              onClick={e => {
                e.stopPropagation();
                aceitar();
              }}
              title="Aceitar a contrapartida sugerida (concilia sem abrir o detalhe)"
            >
              OK
            </Button>
          )}
          {isIa && (
            <Badge
              borderRadius="full"
              px={2}
              fontSize="8px"
              color="white"
              bgGradient="linear(135deg, #6366F1, #EC4899)"
              title="Conciliado pela IA. Alterar manualmente remove esta marca."
            >
              IA
            </Badge>
          )}
          {iaNaoIdentificou && (
            <Tooltip
              label="A IA analisou e não identificou a contrapartida com segurança (comum em Pix a pessoas físicas). Selecione a conta manualmente — a memória aprende e passa a conciliar parecidos sozinha."
              placement="top"
              hasArrow
              fontSize="11px"
              borderRadius="md"
              maxW="250px"
              textAlign="center"
            >
              <Badge
                borderRadius="full"
                px={2}
                py="2px"
                fontSize="8px"
                display="inline-flex"
                alignItems="center"
                gap="3px"
                color="#B45309"
                bg="rgba(245,158,11,0.16)"
                cursor="help"
              >
                <Sparkle size={9} weight="fill" /> IA não identificou
              </Badge>
            </Tooltip>
          )}
          {extratoBloqueado && (
            <Tooltip
              label="O extrato de origem tem divergência não discriminada pelo banco pendente de resolução. Abra o extrato na tela Extratos e resolva o aviso para liberar a conciliação."
              placement="top"
              hasArrow
              fontSize="11px"
              borderRadius="md"
              maxW="250px"
              textAlign="center"
            >
              <Badge
                borderRadius="full"
                px={2}
                py="2px"
                fontSize="8px"
                flexShrink={0}
                display="inline-flex"
                alignItems="center"
                gap="3px"
                color="#B45309"
                bg="rgba(245,158,11,0.16)"
                cursor="help"
              >
                <WarningCircle size={9} weight="fill" /> divergência no extrato
              </Badge>
            </Tooltip>
          )}
          {faltaContaBanco && (
            <Tooltip
              label={`A conta-banco ${lanc.banco_nome ?? ''}${
                lanc.conta ? ` · ${lanc.conta}` : ''
              } não está vinculada a uma POC no plano — por isso fica pendente. Vincule na tela Bancos e reprocesse o extrato.`}
              placement="top"
              hasArrow
              fontSize="11px"
              borderRadius="md"
              maxW="250px"
              textAlign="center"
            >
              <Badge
                borderRadius="full"
                px={2}
                py="2px"
                fontSize="8px"
                flexShrink={0}
                display="inline-flex"
                alignItems="center"
                gap="3px"
                color="#B45309"
                bg="rgba(245,158,11,0.16)"
                cursor="help"
              >
                <WarningCircle size={9} weight="fill" /> sem conta-banco
              </Badge>
            </Tooltip>
          )}
        </Flex>
        {lanc.confianca != null && (
          <Text fontSize="9px" color="#94A3B8" mt="2px">
            {Math.round(lanc.confianca * 100)}% conf.
          </Text>
        )}
      </Td>
    </Tr>
  );
}

// Landing do Conciliar: sem empresa selecionada, lista as empresas com
// pendência (centro de acao /saude) pra escolher uma — em vez de exigir busca.
function EmpresasPendentesLanding({ onPick }: { onPick: (e: IEmpresaSaude) => void }) {
  const { data, isLoading } = useContaCilEmpresasSaude();
  const pendentes = (data?.itens ?? []).filter(e => e.pendentes > 0);
  if (isLoading) {
    return (
      <GlassCard intensity="medium" p={10}>
        <Flex justify="center">
          <Spinner color="#6366F1" />
        </Flex>
      </GlassCard>
    );
  }
  if (!pendentes.length) {
    return (
      <GlassCard intensity="medium" p={10}>
        <Text fontSize="sm" color="#64748B" textAlign="center">
          Nenhuma empresa com pendências de conciliação no momento.
        </Text>
      </GlassCard>
    );
  }
  return (
    <Box>
      <Text fontSize="13px" fontWeight="700" color="#475569" mb={3} px={1}>
        {pendentes.length} {pendentes.length === 1 ? 'empresa' : 'empresas'} com pendências —
        escolha uma pra conciliar
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
        {pendentes.map(e => (
          <EmpresaSaudeCard key={e.empresa_id} item={e} onClick={() => onPick(e)} />
        ))}
      </SimpleGrid>
    </Box>
  );
}

// Card de saldo do Conciliar: SI/SF do mês analisado (ou o mais recente da
// conta) + SF do mês anterior, com check de continuidade (anterior.SF == atual.SI).
function fmtPeriodo(ini: string | null, fim: string | null): string {
  if (!ini) return '';
  const dm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  return fim ? `${dm(ini)}–${dm(fim)}` : dm(ini);
}

function SaldoContaCard({
  empresaId,
  conta,
  ano,
  mes,
}: {
  empresaId?: number;
  conta: string;
  ano?: number;
  mes?: number;
}) {
  const { data: saldos = [] } = useContaCilSaldosConta(empresaId, conta);
  if (!saldos.length) return null;
  let idx = 0;
  if (ano && mes) {
    const mm = String(mes).padStart(2, '0');
    const found = saldos.findIndex(s => s.periodo_ini?.startsWith(`${ano}-${mm}`));
    if (found >= 0) idx = found;
  }
  const atual = saldos[idx];
  const anterior = saldos[idx + 1];
  const continuo =
    anterior && atual && anterior.saldo_final != null && atual.saldo_inicial != null
      ? Math.abs(anterior.saldo_final - atual.saldo_inicial) < 0.01
      : null;
  return (
    <GlassCard intensity="medium" p={4} mb={4}>
      <Flex align="center" gap={8} wrap="wrap">
        <Box>
          <Text
            fontSize="10px"
            fontWeight="700"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            Saldo · período {fmtPeriodo(atual.periodo_ini, atual.periodo_fim)}
          </Text>
          <Flex gap={6} mt={1}>
            <Box>
              <Text fontSize="10px" color="#64748B">
                Inicial
              </Text>
              <Text fontSize="md" fontWeight="700" color="#0F172A">
                {atual.saldo_inicial != null ? `R$ ${fmtValor(atual.saldo_inicial)}` : '—'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="10px" color="#64748B">
                Final
              </Text>
              <Text fontSize="md" fontWeight="700" color="#0F172A">
                {atual.saldo_final != null ? `R$ ${fmtValor(atual.saldo_final)}` : '—'}
              </Text>
            </Box>
          </Flex>
        </Box>
        {anterior && (
          <Box>
            <Text fontSize="10px" color="#64748B">
              Mês anterior fechou em
            </Text>
            <Flex align="center" gap={2} mt={1}>
              <Text fontSize="sm" fontWeight="600" color="#475569">
                {anterior.saldo_final != null ? `R$ ${fmtValor(anterior.saldo_final)}` : '—'}
              </Text>
              {continuo !== null && (
                <Badge
                  colorScheme={continuo ? 'green' : 'orange'}
                  borderRadius="full"
                  fontSize="9px"
                >
                  {continuo ? 'contínuo' : 'divergência'}
                </Badge>
              )}
            </Flex>
          </Box>
        )}
      </Flex>
    </GlassCard>
  );
}

export function ConciliacaoContabilConciliarPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const showDialog = useDialog();
  const [aceitandoTodas, setAceitandoTodas] = useState(false);
  const [destravando, setDestravando] = useState(false);
  // Modo da busca de empresa (toggle): razão (padrão), CNPJ ou id (external_id).
  const [empresaModo, setEmpresaModo] = useState<EmpresaBuscaModo>('razao');
  const carregarEmpresas = useMemo(() => criarBuscaEmpresasModo(empresaModo), [empresaModo]);

  const [empresa, setEmpresa] = useState<IEmpresaOption | null>(null);
  // Aplica `?empresa=<id>` dos atalhos da pagina Empresas (uma vez, no mount).
  useAplicarEmpresaParam(setEmpresa);
  const [status, setStatus] = useState<FiltroStatus>('');
  const [bancoAplicado, setBancoAplicado] = useState('');
  const [contaAplicado, setContaAplicado] = useState('');
  // Um filtro só pra banco/conta: alterna o modo (padrão conta). Trocar limpa
  // o outro, então o filtro envia só o ativo.
  const [bancoModo, setBancoModo] = useState<'conta' | 'banco'>('conta');
  const [ano, setAno] = useState<number | undefined>(undefined);
  const [mes, setMes] = useState<number | undefined>(undefined);
  // Ordem da lista: 'antigos' = mais antigo -> recente (padrão pedido).
  const [ordem, setOrdem] = useState<'recentes' | 'antigos'>('antigos');
  const [searchInput, setSearchInput] = useState('');
  const [searchAplicado, setSearchAplicado] = useState('');
  const [page, setPage] = useState(1);
  const [categoria, setCategoria] = useState<string | null>(null);
  const [tipoOperacao, setTipoOperacao] = useState<string | null>(null);
  const [semContrapartida, setSemContrapartida] = useState(false);
  const [baixaConfianca, setBaixaConfianca] = useState(false);
  const [fonteMatch, setFonteMatch] = useState<string | null>(null);

  const [selecionado, setSelecionado] = useState<IContaCilLancamento | null>(null);
  const [modalAberto, setModalAberto] = useState(false);

  // Debounce dos campos de texto (400ms).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchAplicado(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: pocs = [] } = useContaCilPocs(empresa?.value);
  const pocOptions = useMemo<PocSelectOption[]>(
    () => pocs.map((p: IPocOption) => ({ value: p.codigo, label: `${p.codigo} · ${p.nome}` })),
    [pocs],
  );
  // Mesmas analíticas, agrupadas sob a sintética-pai (cabeçalho não-selecionável).
  const pocGroups = useMemo<PocSelectGroup[]>(() => {
    const byGroup = new Map<string, PocSelectOption[]>();
    const sem: PocSelectOption[] = [];
    pocs.forEach((p: IPocOption) => {
      const opt = { value: p.codigo, label: `${p.codigo} · ${p.nome}` };
      if (p.grupo) {
        const arr = byGroup.get(p.grupo);
        if (arr) arr.push(opt);
        else byGroup.set(p.grupo, [opt]);
      } else {
        sem.push(opt);
      }
    });
    const groups: PocSelectGroup[] = [];
    byGroup.forEach((options, label) => groups.push({ label, options }));
    if (sem.length) groups.push({ label: 'Outras', options: sem });
    return groups;
  }, [pocs]);
  // Bancos da empresa pro seletor (banco_nome = slug canonico; bate o filtro).
  const { data: contasBanco = [] } = useContaCilContas(empresa?.value);
  const bancoOptions = useMemo(
    () =>
      Array.from(new Set(contasBanco.map(c => c.banco_nome).filter(Boolean))).map(n => ({
        value: n,
        label: n,
      })),
    [contasBanco],
  );
  // Contas reais da empresa pro seletor (escopa a tabela numa conta só).
  const contaOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    contasBanco.forEach(c => {
      if (c.conta && !seen.has(c.conta)) {
        seen.add(c.conta);
        opts.push({ value: c.conta, label: `${c.banco_nome} · ${c.conta}` });
      }
    });
    return opts;
  }, [contasBanco]);

  const filtro: ILancamentosFiltro = {
    empresaId: empresa?.value,
    status: status || undefined,
    ano,
    mes,
    banco: bancoAplicado || undefined,
    conta: contaAplicado || undefined,
    search: searchAplicado || undefined,
    categoria: categoria || undefined,
    tipoOperacao: tipoOperacao || undefined,
    semContrapartida: semContrapartida || undefined,
    baixaConfianca: baixaConfianca || undefined,
    fonteMatch: fonteMatch || undefined,
    ordem,
    page,
    size: PAGE_SIZE,
  };
  const { data, isFetching } = useContaCilLancamentos(filtro);

  const itens = data?.items ?? [];
  const totalPages = data?.pages ?? 1;
  const total = data?.total ?? 0;
  const resumo = data?.resumo;

  // Executa o aceite em lote: busca os ids das revisões (paginado) respeitando
  // o filtro atual e confirma via /confirmar-sugeridos (lotes de 500). Cada
  // aceite vira conciliado e ensina a memória.
  const executarAceiteRevisoes = async (empId: number) => {
    const base: Record<string, string | number> = { empresa_id: empId, status: 'revisao' };
    if (filtro.ano) base.ano = filtro.ano;
    if (filtro.mes) base.mes = filtro.mes;
    if (filtro.banco) base.banco = filtro.banco;
    if (filtro.conta) base.conta = filtro.conta;
    if (filtro.search) base.search = filtro.search;
    if (filtro.categoria) base.categoria = filtro.categoria;
    if (filtro.tipoOperacao) base.tipo_operacao = filtro.tipoOperacao;
    if (filtro.dc) base.dc = filtro.dc;
    // Espelho COMPLETO do montarParamsFiltro do hook — qualquer filtro que o
    // resumo (contagem do diálogo) respeita precisa ir no GET dos ids, senão
    // o lote buscaria um superconjunto e aceitaria mais do que foi confirmado.
    if (filtro.valorMin != null) base.valor_min = filtro.valorMin;
    if (filtro.valorMax != null) base.valor_max = filtro.valorMax;
    if (filtro.semContrapartida) base.sem_contrapartida = 'true';
    if (filtro.baixaConfianca) base.baixa_confianca = 'true';
    if (filtro.fonteMatch) base.fonte_match = filtro.fonteMatch;
    if (filtro.iaTentou) base.ia_tentou = 'true';
    setAceitandoTodas(true);
    try {
      let revIds: number[] = [];
      // teto de 100 páginas (20k revisões) por segurança.
      for (let pg = 1; pg <= 100; pg += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await api.get('/api/v1/contacil/lancamentos/', {
          params: { ...base, page: pg, size: 200 },
        });
        const lista = (res.data?.data?.items ?? []) as { id: number }[];
        revIds = revIds.concat(lista.map(i => i.id));
        if (lista.length < 200) break;
      }
      if (revIds.length === 0) {
        setAceitandoTodas(false);
        return;
      }
      let confirmados = 0;
      for (let i = 0; i < revIds.length; i += 500) {
        // eslint-disable-next-line no-await-in-loop
        const res = await api.post('/api/v1/contacil/lancamentos/confirmar-sugeridos', {
          empresa_id: empId,
          ids: revIds.slice(i, i + 500),
        });
        confirmados += (res.data?.data?.confirmados ?? 0) as number;
      }
      queryClient.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
      queryClient.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
      queryClient.invalidateQueries({ queryKey: ['contacil', 'empresas-saude'] });
      const pulados = revIds.length - confirmados;
      toast({
        status: 'success',
        title: `${confirmados} revisão(ões) aceita(s)`,
        description:
          pulados > 0
            ? `${pulados} não pôde(ram) ser aceita(s) — sem conta-banco ou extrato com divergência pendente.`
            : 'Viraram conciliado e a memória aprendeu.',
      });
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao aceitar as revisões',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    } finally {
      setAceitandoTodas(false);
    }
  };

  // Abre o confirm estilizado (provider de diálogo) antes do aceite em lote.
  const aceitarTodasRevisoes = () => {
    const empId = empresa?.value;
    const nRev = resumo?.revisao ?? 0;
    if (!empId || nRev === 0) return;
    showDialog({
      title: 'Aceitar revisões em lote',
      status: 'warning',
      description: `Aceitar ${nRev} lançamento(s) em revisão? Eles viram conciliado e a memória aprende com a sua escolha.`,
      confirmLabel: 'Aceitar todas',
      cancelLabel: 'Cancelar',
      onConfirm: () => {
        executarAceiteRevisoes(empId);
      },
    });
  };

  // Devolve pra 'pendente' os 'revisao' SEM contrapartida — o motor nao achou
  // a contraparte, entao nem 'Aceitar todas' (exige 2 pernas) nem a IA
  // (so' pega pendente) os alcancam. Destravar libera o fluxo normal.
  const destravarRevisoes = () => {
    const empId = empresa?.value;
    if (!empId) return;
    showDialog({
      title: 'Destravar revisões para classificar',
      status: 'warning',
      description:
        'Os lançamentos em revisão sem contrapartida (o motor não achou a conta) voltam para pendente. Faça isso só depois de conferir o extrato — aí o Auto-conciliar (IA) e as regras passam a alcançá-los.',
      confirmLabel: 'Destravar',
      cancelLabel: 'Cancelar',
      onConfirm: async () => {
        setDestravando(true);
        try {
          const res = await api.post('/api/v1/contacil/lancamentos/destravar-revisao', {
            empresa_id: empId,
          });
          const n = (res.data?.data?.destravados ?? 0) as number;
          queryClient.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
          queryClient.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
          queryClient.invalidateQueries({ queryKey: ['contacil', 'empresas-saude'] });
          toast({
            status: n > 0 ? 'success' : 'info',
            title: n > 0 ? `${n} lançamento(s) destravado(s)` : 'Nada para destravar',
            description:
              n > 0
                ? 'Viraram pendente — use o Auto-conciliar (IA) ou regras para classificar.'
                : 'Não há revisão sem contrapartida nesta empresa.',
          });
        } catch (err) {
          toast({
            status: 'error',
            title: 'Falha ao destravar',
            description: extractApiErrorMessage(err, 'Tente novamente.'),
          });
        } finally {
          setDestravando(false);
        }
      },
    });
  };

  const ids = useMemo(() => itens.map(i => i.id), [itens]);
  const { data: notasMap } = useContaCilNotasSugeridas(empresa?.value, ids);

  // Modal sincronizado com a lista viva (reflete o POC salvo inline no refetch).
  const modalLanc = selecionado ? (itens.find(i => i.id === selecionado.id) ?? selecionado) : null;

  const recarregar = () => {
    queryClient.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
    queryClient.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
  };

  const abrirDetalhe = (lanc: IContaCilLancamento) => {
    setSelecionado(lanc);
    setModalAberto(true);
  };

  const selecionarStatus = (value: FiltroStatus) => {
    setStatus(prev => (prev === value && value !== '' ? '' : value));
    setPage(1);
  };

  // Auto-conciliar agora abre um modal (slider de %, estimativa de custo,
  // barra de progresso) que orquestra a conciliacao em lotes.
  const autoConc = useDisclosure();

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" justify="space-between" gap={3} mb={5} wrap="wrap">
            <Flex align="center" gap={3}>
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} weight="bold" />}
                variant="ghost"
                size="sm"
                borderRadius="full"
                color="#64748B"
                _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
                onClick={() => navigate('/modulesHome/accountingConciliation/conciliacao-contabil')}
              />
              <Box>
                <Text
                  fontSize="11px"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                  fontWeight="semibold"
                >
                  Conciliação Contábil
                </Text>
                <Heading fontSize={{ base: '22px', md: '28px' }} fontWeight="700" color="#0F172A">
                  Conciliar
                </Heading>
              </Box>
            </Flex>
            {empresa && (
              <Flex align="center" gap={2} wrap="wrap">
                <Button
                  leftIcon={<ListChecks size={16} weight="bold" />}
                  variant="outline"
                  borderRadius="full"
                  px={5}
                  fontWeight="600"
                  color="#6366F1"
                  borderColor="rgba(99,102,241,0.35)"
                  bg="rgba(255,255,255,0.6)"
                  _hover={{ bg: 'rgba(99,102,241,0.08)', borderColor: '#6366F1' }}
                  title={`Abrir as regras de ${empresa.label} em nova aba`}
                  onClick={() =>
                    window.open(
                      `/modulesHome/accountingConciliation/conciliacao-contabil/regras?empresa=${empresa.value}`,
                      '_blank',
                      'noopener',
                    )
                  }
                >
                  Regras
                </Button>
                <Button
                  leftIcon={<Sparkle size={16} weight="fill" />}
                  borderRadius="full"
                  px={5}
                  color="white"
                  fontWeight="600"
                  bgGradient="linear(135deg, #6366F1, #EC4899)"
                  boxShadow="0 6px 16px -4px rgba(99,102,241,0.4)"
                  _hover={{ filter: 'brightness(1.05)', transform: 'translateY(-1px)' }}
                  onClick={autoConc.onOpen}
                >
                  Auto-conciliar (IA)
                </Button>
              </Flex>
            )}
          </Flex>
        </motion.div>

        {/* KPIs (clicáveis = filtram status) — só com empresa */}
        {empresa && (
          <ConciliarKpis
            resumo={resumo}
            statusAtivo={status}
            onSelect={selecionarStatus}
            isLoading={isFetching}
          />
        )}

        {empresa && resumo && <ProgressoConciliacao resumo={resumo} />}

        {empresa && contaAplicado && (
          <SaldoContaCard empresaId={empresa.value} conta={contaAplicado} ano={ano} mes={mes} />
        )}

        {empresa && (
          <Flex align="center" gap={2} mb={5} wrap="wrap">
            <Text fontSize="11px" fontWeight="600" color="#94A3B8" textTransform="uppercase" mr={1}>
              A resolver
            </Text>
            {[
              {
                lbl: 'Sem contrapartida',
                on: semContrapartida,
                toggle: () => {
                  setSemContrapartida(v => !v);
                  setPage(1);
                },
              },
              {
                lbl: 'Baixa confiança',
                on: baixaConfianca,
                toggle: () => {
                  setBaixaConfianca(v => !v);
                  setPage(1);
                },
              },
              {
                lbl: 'Só IA',
                on: fonteMatch === 'ia',
                toggle: () => {
                  setFonteMatch(v => (v === 'ia' ? null : 'ia'));
                  setPage(1);
                },
              },
            ].map(p => (
              <Box
                key={p.lbl}
                as="button"
                onClick={p.toggle}
                aria-pressed={p.on}
                px={3}
                py={1}
                borderRadius="full"
                fontSize="12px"
                fontWeight="600"
                borderWidth="1px"
                borderColor={p.on ? '#6366F1' : 'rgba(15, 23, 42, 0.1)'}
                bg={p.on ? 'rgba(99, 102, 241, 0.1)' : 'white'}
                color={p.on ? '#6366F1' : '#64748B'}
              >
                {p.lbl}
              </Box>
            ))}
            {categoria && (
              <Badge
                colorScheme="purple"
                borderRadius="full"
                px={2}
                cursor="pointer"
                onClick={() => {
                  setCategoria(null);
                  setPage(1);
                }}
              >
                categoria: {categoria} ✕
              </Badge>
            )}
            {tipoOperacao && (
              <Badge
                colorScheme="blue"
                borderRadius="full"
                px={2}
                cursor="pointer"
                onClick={() => {
                  setTipoOperacao(null);
                  setPage(1);
                }}
              >
                tipo: {tipoOperacao} ✕
              </Badge>
            )}
          </Flex>
        )}

        {empresa && (
          <QuebrasPanel
            filtro={filtro}
            onCategoria={c => {
              setCategoria(c);
              setPage(1);
            }}
            onTipo={c => {
              setTipoOperacao(c);
              setPage(1);
            }}
            onBanco={c => {
              setBancoModo('banco');
              setBancoAplicado(c ?? '');
              setPage(1);
            }}
          />
        )}

        {/* Barra: empresa + filtros */}
        <GlassCard intensity="medium" p={4} mb={5}>
          <Flex gap={3} wrap="wrap" align={{ md: 'flex-end' }}>
            <Box flex={1} minW="220px">
              <Flex align="center" justify="space-between" mb={1} gap={2}>
                <Text fontSize="10px" fontWeight="600" color="#64748B" textTransform="uppercase">
                  Empresa
                </Text>
                <Flex bg="rgba(15,23,42,0.06)" borderRadius="md" p="2px" gap="2px">
                  {(
                    [
                      ['razao', 'Razão'],
                      ['cnpj', 'CNPJ'],
                      ['id', 'Id'],
                    ] as const
                  ).map(([m, lbl]) => (
                    <Box
                      key={m}
                      as="button"
                      type="button"
                      px={2}
                      py="1px"
                      borderRadius="sm"
                      fontSize="9px"
                      fontWeight="700"
                      textTransform="uppercase"
                      color={empresaModo === m ? '#0F172A' : '#94A3B8'}
                      bg={empresaModo === m ? 'white' : 'transparent'}
                      boxShadow={empresaModo === m ? 'sm' : 'none'}
                      onClick={() => setEmpresaModo(m)}
                    >
                      {lbl}
                    </Box>
                  ))}
                </Flex>
              </Flex>
              <AsyncSelect<IEmpresaOption>
                size="sm"
                placeholder={PLACEHOLDER_BUSCA_EMPRESA[empresaModo]}
                loadOptions={carregarEmpresas}
                value={empresa}
                onChange={opt => {
                  setEmpresa(opt as IEmpresaOption | null);
                  // Reseta os filtros pra não carregar texto/estado da empresa anterior.
                  setStatus('');
                  setBancoAplicado('');
                  setContaAplicado('');
                  setSearchInput('');
                  setSearchAplicado('');
                  setAno(undefined);
                  setMes(undefined);
                  setCategoria(null);
                  setTipoOperacao(null);
                  setSemContrapartida(false);
                  setBaixaConfianca(false);
                  setFonteMatch(null);
                  setPage(1);
                }}
                loadingMessage={() => 'Buscando...'}
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? 'Nenhuma empresa encontrada' : 'Digite para buscar'
                }
                isClearable
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                {...propsMenuSelect}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            </Box>
            <Box w={{ base: '100%', md: '210px' }}>
              <Flex align="center" justify="space-between" mb={1} gap={2}>
                <Text fontSize="10px" fontWeight="600" color="#64748B" textTransform="uppercase">
                  {bancoModo === 'conta' ? 'Conta' : 'Banco'}
                </Text>
                <Flex bg="rgba(15,23,42,0.06)" borderRadius="md" p="2px" gap="2px">
                  {(['conta', 'banco'] as const).map(m => (
                    <Box
                      key={m}
                      as="button"
                      type="button"
                      px={2}
                      py="1px"
                      borderRadius="sm"
                      fontSize="9px"
                      fontWeight="700"
                      textTransform="uppercase"
                      color={bancoModo === m ? '#0F172A' : '#94A3B8'}
                      bg={bancoModo === m ? 'white' : 'transparent'}
                      boxShadow={bancoModo === m ? 'sm' : 'none'}
                      onClick={() => {
                        setBancoModo(m);
                        setBancoAplicado('');
                        setContaAplicado('');
                        setPage(1);
                      }}
                    >
                      {m}
                    </Box>
                  ))}
                </Flex>
              </Flex>
              {bancoModo === 'conta' ? (
                <RSelect<{ value: string; label: string }>
                  size="sm"
                  placeholder="Todas"
                  options={contaOptions}
                  value={contaOptions.find(o => o.value === contaAplicado) ?? null}
                  onChange={opt => {
                    setContaAplicado(opt ? opt.value : '');
                    setPage(1);
                  }}
                  isClearable
                  isDisabled={!empresa}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  {...propsMenuSelect}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                  chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
                />
              ) : (
                <RSelect<{ value: string; label: string }>
                  size="sm"
                  placeholder="Todos"
                  options={bancoOptions}
                  value={bancoOptions.find(o => o.value === bancoAplicado) ?? null}
                  onChange={opt => {
                    setBancoAplicado(opt ? opt.value : '');
                    setPage(1);
                  }}
                  isClearable
                  isDisabled={!empresa}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  {...propsMenuSelect}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                  chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
                />
              )}
            </Box>
            <Box w={{ base: '48%', md: '120px' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Ano
              </Text>
              <ChakraSelect
                size="sm"
                borderRadius="lg"
                bg="white"
                value={ano ?? ''}
                onChange={e => {
                  setAno(e.target.value ? Number(e.target.value) : undefined);
                  setPage(1);
                }}
                isDisabled={!empresa}
              >
                <option value="">Todos</option>
                {ANOS.map(a => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </ChakraSelect>
            </Box>
            <Box w={{ base: '48%', md: '140px' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Mês
              </Text>
              <ChakraSelect
                size="sm"
                borderRadius="lg"
                bg="white"
                value={mes ?? ''}
                onChange={e => {
                  setMes(e.target.value ? Number(e.target.value) : undefined);
                  setPage(1);
                }}
                isDisabled={!empresa}
              >
                <option value="">Todos</option>
                {MESES.map((nome, i) => (
                  <option key={nome} value={i + 1}>
                    {nome}
                  </option>
                ))}
              </ChakraSelect>
            </Box>
            <Box flex={1} minW="200px">
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Buscar no histórico
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <MagnifyingGlass size={14} color="#94A3B8" />
                </InputLeftElement>
                <Input
                  borderRadius="lg"
                  bg="white"
                  placeholder="PIX, TED, fornecedor..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  isDisabled={!empresa}
                />
              </InputGroup>
            </Box>
          </Flex>
        </GlassCard>

        {/* Conteúdo */}
        {!empresa ? (
          <EmpresasPendentesLanding
            onPick={e => {
              setEmpresa({
                value: e.empresa_id,
                label: e.razao_social || e.nome || `Empresa ${e.empresa_id}`,
                external_id: '',
              });
              setPage(1);
            }}
          />
        ) : (
          <>
            {/* Desktop: tabela */}
            <GlassCard
              intensity="medium"
              p={0}
              overflow="hidden"
              display={{ base: 'none', md: 'block' }}
            >
              <Flex
                align="center"
                justify="space-between"
                gap={3}
                wrap="wrap"
                px={4}
                py={3}
                borderBottomWidth="1px"
                borderColor="rgba(15, 23, 42, 0.06)"
              >
                <Text fontSize="sm" fontWeight="600" color="#0F172A">
                  {total} lançamento(s)
                  {isFetching && <Spinner size="xs" ml={2} color="#1B88F5" thickness="2px" />}
                </Text>
                <Flex align="center" gap={3} wrap="wrap">
                  {resumo?.periodo_ini && resumo?.periodo_fim && (
                    <Text fontSize="11px" color="#64748B">
                      {fmtData(resumo.periodo_ini)} – {fmtData(resumo.periodo_fim)}
                    </Text>
                  )}
                  <Flex
                    align="center"
                    gap="2px"
                    bg="rgba(15, 23, 42, 0.04)"
                    borderRadius="full"
                    p="3px"
                  >
                    {(
                      [
                        { v: 'antigos', label: 'Antigos' },
                        { v: 'recentes', label: 'Recentes' },
                      ] as const
                    ).map(o => (
                      <Button
                        key={o.v}
                        size="xs"
                        h="26px"
                        px={3}
                        borderRadius="full"
                        variant="unstyled"
                        fontWeight="600"
                        fontSize="12px"
                        bg={ordem === o.v ? 'white' : 'transparent'}
                        color={ordem === o.v ? '#6366F1' : '#64748B'}
                        boxShadow={
                          ordem === o.v ? '0 1px 4px -1px rgba(15, 23, 42, 0.15)' : undefined
                        }
                        onClick={() => {
                          setOrdem(o.v);
                          setPage(1);
                        }}
                      >
                        {o.label}
                      </Button>
                    ))}
                  </Flex>
                </Flex>
              </Flex>

              {(resumo?.revisao ?? 0) > 0 && (
                <Flex
                  align="center"
                  justify="space-between"
                  gap={3}
                  wrap="wrap"
                  px={4}
                  py={2.5}
                  bg="rgba(245, 158, 11, 0.08)"
                  borderBottomWidth="1px"
                  borderColor="rgba(245, 158, 11, 0.22)"
                >
                  <Flex align="center" gap={2}>
                    <WarningCircle size={16} weight="fill" color="#D97706" />
                    <Text fontSize="13px" fontWeight="600" color="#92400E">
                      {resumo?.revisao} em revisão — o motor preencheu e marcou pra você conferir
                    </Text>
                  </Flex>
                  <Flex align="center" gap={2}>
                    <Tooltip
                      label="Devolve para pendente os que estão em revisão sem contrapartida (o motor não achou a conta) — aí o Auto-conciliar (IA) e as regras conseguem classificá-los."
                      hasArrow
                      placement="top"
                      maxW="280px"
                      fontSize="11px"
                    >
                      <Button
                        size="sm"
                        h="30px"
                        borderRadius="full"
                        variant="outline"
                        colorScheme="orange"
                        leftIcon={<LockSimpleOpen size={14} weight="bold" />}
                        isLoading={destravando}
                        onClick={destravarRevisoes}
                      >
                        Destravar p/ classificar
                      </Button>
                    </Tooltip>
                    <Button
                      size="sm"
                      h="30px"
                      borderRadius="full"
                      colorScheme="green"
                      leftIcon={<CheckCircle size={14} weight="fill" />}
                      isLoading={aceitandoTodas}
                      onClick={aceitarTodasRevisoes}
                    >
                      Aceitar todas
                    </Button>
                  </Flex>
                </Flex>
              )}

              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead bg="rgba(15, 23, 42, 0.02)">
                    <Tr>
                      <Th>Data</Th>
                      <Th>Histórico</Th>
                      <Th isNumeric>Valor</Th>
                      <Th isNumeric>Saldo</Th>
                      <Th>Contrapartida (POC)</Th>
                      <Th>Contraparte / NF</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {itens.map(lanc => (
                      <LinhaLancamento
                        key={lanc.id}
                        lanc={lanc}
                        pocOptions={pocOptions}
                        pocGroups={pocGroups}
                        nota={notasMap?.[lanc.id]}
                        onSaved={recarregar}
                        onOpen={abrirDetalhe}
                      />
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {itens.length === 0 && (
                <Box py={10}>
                  <Text fontSize="sm" color="#94A3B8" textAlign="center">
                    Nenhum lançamento para os filtros atuais.
                  </Text>
                </Box>
              )}
            </GlassCard>

            {/* Mobile: cards */}
            <Box display={{ base: 'block', md: 'none' }}>
              <Flex align="center" gap={2} mb={3} px={1}>
                <Text fontSize="sm" fontWeight="600" color="#0F172A">
                  {total} lançamento(s)
                </Text>
                {isFetching && <Spinner size="xs" color="#1B88F5" thickness="2px" />}
              </Flex>
              <Flex direction="column" gap={3}>
                {itens.map((lanc, idx) => (
                  <LancamentoCard
                    key={lanc.id}
                    lanc={lanc}
                    index={idx}
                    pocOptions={pocOptions}
                    pocGroups={pocGroups}
                    onSaved={recarregar}
                    onOpen={abrirDetalhe}
                    empresaId={empresa?.value}
                  />
                ))}
              </Flex>
              {itens.length === 0 && (
                <GlassCard intensity="medium" p={10} mt={3}>
                  <Text fontSize="sm" color="#94A3B8" textAlign="center">
                    Nenhum lançamento para os filtros atuais.
                  </Text>
                </GlassCard>
              )}
            </Box>

            {/* Paginação */}
            {totalPages > 1 && (
              <Flex align="center" justify="center" gap={3} mt={6}>
                <IconButton
                  aria-label="Página anterior"
                  icon={<CaretLeft size={14} weight="bold" />}
                  size="xs"
                  variant="ghost"
                  borderRadius="full"
                  isDisabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                />
                <Text fontSize="xs" color="#64748B" fontWeight="600">
                  {page} / {totalPages}
                </Text>
                <IconButton
                  aria-label="Próxima página"
                  icon={<CaretRight size={14} weight="bold" />}
                  size="xs"
                  variant="ghost"
                  borderRadius="full"
                  isDisabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                />
              </Flex>
            )}
          </>
        )}
      </Box>

      <LancamentoDetailModal
        lanc={modalLanc}
        pocOptions={pocOptions}
        pocGroups={pocGroups}
        nota={modalLanc ? notasMap?.[modalLanc.id] : undefined}
        isOpen={modalAberto}
        onClose={() => {
          setModalAberto(false);
          setSelecionado(null);
        }}
        onSaved={recarregar}
        empresaId={empresa?.value ?? null}
      />
      <AutoConciliarModal
        isOpen={autoConc.isOpen}
        onClose={autoConc.onClose}
        empresaId={empresa?.value ?? null}
        empresaLabel={empresa?.label}
        // O mesmo recorte da lista e dos KPIs: a ação em massa passa a tocar
        // exatamente o que está na tela, não a empresa inteira.
        recorte={{
          ano,
          mes,
          banco: bancoAplicado || undefined,
          conta: contaAplicado || undefined,
        }}
      />
    </GlassPageBg>
  );
}
